import React, { useId } from 'react';
import { useTranslation } from 'react-i18next';
import { formatCurrency } from '../../utils/formatCurrency';
import ElapsedTimer from '../ElapsedTimer';
import useFocusTrap from '../../hooks/useFocusTrap';
import AssignRiderControl from './AssignRiderControl';
import './OrderDetailModal.css';

const STATUS_COLORS = {
  pending: '#ff9800',
  preparing: '#2196F3',
  ready: '#A0F487',
  delivered: '#8C92A5',
};

const PLATFORM_COLORS = {
  pedidosya: '#FF0066',
  rappi: '#FF5A00',
  ubereats: '#06C167',
  glovo: '#FFC244',
};

const PAYMENT_METHOD_CONFIG = {
  cash: { icon: 'payments', labelKey: 'orders.markPaidCash', defaultLabel: 'Cobrar efectivo' },
  card: { icon: 'credit_card', labelKey: 'orders.markPaidCard', defaultLabel: 'Cobrar tarjeta' },
  transfer: { icon: 'account_balance', labelKey: 'orders.markPaidTransfer', defaultLabel: 'Cobrar transferencia' },
  mercadopago: { icon: 'qr_code_2', labelKey: 'orders.markPaidMercadoPago', defaultLabel: 'Cobrar Mercado Pago' },
};

function OrderDetailModal({ order, isOpen, onClose, onMarkPaid, onEditOrder, onCancelOrder, paymentMethods, onRiderAssigned }) {
  const { t } = useTranslation();
  const trapRef = useFocusTrap();
  const titleId = useId();

  if (!isOpen || !order) return null;

  // Derive enabled payment methods from restaurant config
  const enabledMethods = paymentMethods
    ? Object.entries(paymentMethods)
        .filter(([, config]) => config.enabled)
        .map(([key]) => key)
        .filter((key) => PAYMENT_METHOD_CONFIG[key])
    : ['cash', 'card'];

  const formatTime = (isoString) => {
    const date = new Date(isoString);
    return date.toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  };

  const formatDate = (isoString) => {
    const date = new Date(isoString);
    return date.toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const statusColor = STATUS_COLORS[order.status];
  const isDelivery = order.orderType === 'delivery';
  const platformColor = isDelivery ? PLATFORM_COLORS[order.deliveryPlatform] || '#666' : null;

  return (
    <div className="order-modal-overlay" onClick={onClose}>
      <div
        ref={trapRef}
        className={`order-modal ${isDelivery ? 'order-modal-delivery' : ''}`}
        role="dialog"
        aria-labelledby={titleId}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="order-modal-header" style={{ borderLeftColor: isDelivery ? platformColor : statusColor }}>
          <div className="order-modal-title">
            {isDelivery ? (
              <h2 id={titleId} className="order-modal-delivery-title">
                <span className="order-modal-delivery-badge" style={{ backgroundColor: platformColor }}>
                  🛵 {order.deliveryPlatform?.toUpperCase() || 'DELIVERY'}
                </span>
              </h2>
            ) : (
              <h2 id={titleId}>
                {order.orderType === 'takeaway'
                  ? t('orders.takeaway', 'Takeaway')
                  : `${t('orders.table')} ${order.tableNumber}`}
              </h2>
            )}
            <span className="order-modal-id">#{order.orderId.slice(-6)}</span>
          </div>
          <button className="order-modal-close" onClick={onClose} aria-label={t('common.close', 'Close')}>
            <span className="material-icons" aria-hidden="true">close</span>
          </button>
        </div>

        {/* Status Badge */}
        <div className="order-modal-status">
          <span
            className="order-modal-status-badge"
            style={{ backgroundColor: statusColor }}
          >
            {t(`orders.status.${order.status}`)}
          </span>
          <ElapsedTimer
            createdAt={order.createdAt}
            render={(elapsedMinutes) =>
              elapsedMinutes > 0 ? (
                <span className={`order-modal-elapsed ${elapsedMinutes > 15 ? 'urgent' : ''}`}>
                  {t('orders.minutes', { count: elapsedMinutes })}
                </span>
              ) : null
            }
          />
        </div>

        {/* Time Info */}
        <div className="order-modal-info">
          <div className="order-modal-info-row">
            <span className="material-icons" aria-hidden="true">schedule</span>
            <div className="order-modal-info-text">
              <span className="order-modal-info-label">{t('orders.createdAt', 'Hora de creación')}</span>
              <span className="order-modal-info-value">{formatDate(order.createdAt)} - {formatTime(order.createdAt)}</span>
            </div>
          </div>

          {order.customerName && (
            <div className="order-modal-info-row">
              <span className="material-icons" aria-hidden="true">person</span>
              <div className="order-modal-info-text">
                <span className="order-modal-info-label">{t('orders.customer', 'Cliente')}</span>
                <span className="order-modal-info-value">{order.customerName}</span>
              </div>
            </div>
          )}

          {order.customerPhone && (
            <div className="order-modal-info-row">
              <span className="material-icons" aria-hidden="true">phone</span>
              <div className="order-modal-info-text">
                <span className="order-modal-info-label">{t('orders.phone', 'Teléfono')}</span>
                <span className="order-modal-info-value">{order.customerPhone}</span>
              </div>
            </div>
          )}
        </div>

        {/* Delivery Info Section */}
        {isDelivery && (
          <div className="order-modal-delivery-section">
            <h3 className="order-modal-section-title">
              <span className="material-icons" aria-hidden="true">local_shipping</span>
              {t('orders.delivery.title', 'Información de Delivery')}
            </h3>

            {order.deliveryAddress && (
              <div className="order-modal-info-row">
                <span className="material-icons" aria-hidden="true">location_on</span>
                <div className="order-modal-info-text">
                  <span className="order-modal-info-label">{t('orders.delivery.address', 'Dirección')}</span>
                  <span className="order-modal-info-value">
                    {order.deliveryAddress.formatted || order.deliveryAddress.street || '-'}
                  </span>
                  {order.deliveryAddress.apartment && (
                    <span className="order-modal-info-secondary">
                      {t('orders.delivery.apartment', 'Dpto')}: {order.deliveryAddress.apartment}
                    </span>
                  )}
                  {order.deliveryAddress.instructions && (
                    <span className="order-modal-info-secondary">
                      📝 {order.deliveryAddress.instructions}
                    </span>
                  )}
                </div>
              </div>
            )}

            {(order.riderInfo || order.deliveryRider) && (
              <div className="order-modal-info-row">
                <span className="material-icons" aria-hidden="true">two_wheeler</span>
                <div className="order-modal-info-text">
                  <span className="order-modal-info-label">{t('orders.delivery.rider', 'Repartidor')}</span>
                  <span className="order-modal-info-value">
                    {(order.riderInfo || order.deliveryRider).name || '-'}
                  </span>
                  {(order.riderInfo || order.deliveryRider).phone && (
                    <span className="order-modal-info-secondary">
                      📞 {(order.riderInfo || order.deliveryRider).phone}
                    </span>
                  )}
                </div>
              </div>
            )}

            {/* MVP: asignar repartidor propio si aún no hay uno (NER-1237) */}
            {!order.riderInfo && !order.deliveryRider && (
              <AssignRiderControl order={order} onAssigned={onRiderAssigned} />
            )}

            {order.estimatedDeliveryTime && (
              <div className="order-modal-info-row">
                <span className="material-icons" aria-hidden="true">schedule</span>
                <div className="order-modal-info-text">
                  <span className="order-modal-info-label">{t('orders.delivery.estimatedTime', 'Entrega estimada')}</span>
                  <span className="order-modal-info-value">{formatTime(order.estimatedDeliveryTime)}</span>
                </div>
              </div>
            )}

            {order.deliveryStatus && order.deliveryStatus !== order.status && (
              <div className="order-modal-delivery-status">
                <span className="order-modal-info-label">{t('orders.delivery.status', 'Estado delivery')}</span>
                <span className="order-modal-delivery-status-badge">
                  {t(`orders.delivery.statusTypes.${order.deliveryStatus}`, order.deliveryStatus)}
                </span>
              </div>
            )}
          </div>
        )}

        {/* Items List */}
        <div className="order-modal-items">
          <h3 className="order-modal-section-title">
            {t('orders.items', 'Items')} ({order.items?.length || 0})
          </h3>
          <div className="order-modal-items-list">
            {order.items?.map((item, idx) => {
              // Support both modifiers and selectedModifiers field names
              const modifiers = item.selectedModifiers || item.modifiers || [];
              return (
                <div key={idx} className="order-modal-item">
                  <div className="order-modal-item-header">
                    <span className="order-modal-item-qty">{item.quantity}x</span>
                    <span className="order-modal-item-name">{item.name}</span>
                    <span className="order-modal-item-price">{formatCurrency(item.price)}</span>
                  </div>
                  {modifiers.length > 0 && (
                    <div className="order-modal-item-modifiers">
                      {modifiers.map((mod, modIdx) => (
                        <span key={modIdx} className="order-modal-item-modifier">
                          + {mod.optionName || mod.name} {(mod.price > 0) && `(+${formatCurrency(mod.price)})`}
                        </span>
                      ))}
                    </div>
                  )}
                  {item.notes && (
                    <div className="order-modal-item-notes">
                      <span className="material-icons" aria-hidden="true">chat</span>
                      <span>{item.notes}</span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Total */}
        <div className="order-modal-total">
          <div className="order-modal-total-row">
            <span>{t('orders.subtotal', 'Subtotal')}</span>
            <span>{formatCurrency(order.subtotal)}</span>
          </div>
          {order.serviceCharge > 0 && (
            <div className="order-modal-total-row">
              <span>{t('orders.serviceCharge', 'Servicio')} (10%)</span>
              <span>{formatCurrency(order.serviceCharge)}</span>
            </div>
          )}
          {order.tax > 0 && (
            <div className="order-modal-total-row">
              <span>{t('orders.tax', 'IVA')} (21%)</span>
              <span>{formatCurrency(order.tax)}</span>
            </div>
          )}
          {order.discount && (
            <div className="order-modal-total-row discount">
              <span>
                {t('orders.discount', 'Descuento')}
                {order.discount.type === 'percentage' && ` (${order.discount.value}%)`}
              </span>
              <span>-{formatCurrency(order.discount.type === 'percentage'
                ? order.subtotal * order.discount.value / 100
                : order.discount.value)}</span>
            </div>
          )}
          {order.tip && (
            <div className="order-modal-total-row tip">
              <span>{t('orders.tip', 'Propina')}</span>
              <span>{formatCurrency(order.tip.amount)}</span>
            </div>
          )}
          <div className="order-modal-total-row total">
            <span>{t('orders.total', 'Total')}</span>
            <span>{formatCurrency(order.total)}</span>
          </div>
        </div>

        {/* Payment Info */}
        {order.paymentMethod && (
          <div className="order-modal-payment">
            <span className="material-icons" aria-hidden="true">payment</span>
            <span>{t(`payment.${order.paymentMethod}`, order.paymentMethod)}</span>
          </div>
        )}

        {/* Edit / Cancel actions (only for pending orders) */}
        {order.status === 'pending' && (onEditOrder || onCancelOrder) && (
          <div className="order-modal-pending-actions">
            {onEditOrder && (
              <button
                className="order-modal-edit-btn"
                onClick={() => onEditOrder(order)}
              >
                <span className="material-icons" aria-hidden="true">edit</span>
                {t('orders.editOrder')}
              </button>
            )}
            {onCancelOrder && (
              <button
                className="order-modal-cancel-btn"
                onClick={() => onCancelOrder(order)}
              >
                <span className="material-icons" aria-hidden="true">delete</span>
                {t('orders.cancelOrder')}
              </button>
            )}
          </div>
        )}

        {/* Mark as Paid */}
        {onMarkPaid && order.paymentStatus !== 'paid' && (
          <div className="order-modal-mark-paid">
            {enabledMethods.map((method) => {
              const config = PAYMENT_METHOD_CONFIG[method];
              const label = t(config.labelKey, config.defaultLabel);
              const shortId = order.orderId.slice(-6);
              const ariaLabel = t('orders.markPaidAriaLabel', '{{method}} pedido #{{orderId}}', { method: label, orderId: shortId });
              return (
                <button
                  key={method}
                  className="order-modal-mark-paid-btn"
                  onClick={() => onMarkPaid(order, method)}
                  aria-label={ariaLabel}
                >
                  <span className="material-icons" aria-hidden="true">{config.icon}</span>
                  {label}
                </button>
              );
            })}
          </div>
        )}

        {order.paymentStatus === 'paid' && (
          <div className="order-modal-paid-badge">
            <span className="material-icons" aria-hidden="true">check_circle</span>
            {t('orders.paid', 'Pagado')}
          </div>
        )}
      </div>
    </div>
  );
}

export default OrderDetailModal;
